/* ==========================================================================
   Desktop Sticky Cart — Review + Payment + order placement (parallel build).

   Mirrors the existing addressSummaryPage.liquid flow with the SAME backends:
     • Payment methods ← POST /web/v1/juspay/payment-methods  { items, AOV, couponTitle }
     • Juspay initiate ← POST /web/v1/juspay/initiate         → redirect payment_links.web
     • COD order       ← POST /order/v3/create-cod-order

   ⚠️ MONEY PATH — GUARDED. Order placement (createCOD / Juspay initiate) is the
   real checkout. Because the exact payloads (full attributionData, fee/amount
   units, order-success redirect) are reconstructed and can't be verified here,
   placeOrder() is gated behind window.STICKY_CART_ALLOW_ORDER. Set it to true
   only after verifying the payloads against a live test order. Until then the
   buttons render and select, but the final call is blocked with a clear notice.

   Exposes window.StickyCartCheckout.renderReview(scope) / renderPayment(scope).
   ========================================================================== */
(function () {
  'use strict';

  var PAYMENT_METHODS_URL = 'https://supertails-backend.el.r.appspot.com/web/v1/juspay/payment-methods';
  var JUSPAY_INITIATE_URL = 'https://supertails-backend.el.r.appspot.com/web/v1/juspay/initiate';
  var COD_ORDER_URL = 'https://supertails-backend.el.r.appspot.com/order/v3/create-cod-order';
  var THANKYOU_URL = 'https://supertails-ui--supertails-ui.asia-east1.hosted.app/orders/thank-you';

  // Customer (id/email/phone) — read from the shell's page-context emit (the section is
  // fetched via the Section Rendering API where `customer` isn't reliably populated).
  function shellCustomer() {
    try {
      var el = document.querySelector('[data-st-offers-customer]');
      if (el) { var j = JSON.parse(el.textContent); return (j && j.customer) || {}; }
    } catch (e) {}
    return {};
  }

  // Shipping address + email, same precedence/mapping as addressSummaryPage 2330-2369.
  function buildShippingAddress() {
    var cust = shellCustomer();
    function parse(k, store) { try { return JSON.parse((store || sessionStorage).getItem(k)); } catch (e) { return null; } }
    var guest = parse('guestAddress');
    if (guest) return { addr: guest, email: guest.email };
    var ord = parse('order_shipping_address');
    if (ord) return { addr: ord, email: cust.email };
    var loc = parse('userLocation', localStorage);
    if (loc) {
      var parts = (loc.name || '').trim().split(' ');
      return {
        addr: {
          id: loc.addressId || null,
          first_name: parts[0] || '',
          last_name: parts.slice(1).join(' ') || '',
          name: loc.name || '',
          phone: loc.phone || '',
          building_number: loc.house || '',
          address1: loc.area || loc.house || '',
          address2: loc.fullAddress || '',
          city: loc.city || '',
          province: loc.state || '',
          zip: loc.pincode || '',
          country: 'India',
          latitude: loc.latitude || null,
          longitude: loc.longitude || null,
          default: loc.default || false,
          customer_id: cust.id || null,
          is_confirmed: !!(loc.latitude && loc.longitude)
        },
        email: cust.email
      };
    }
    return { addr: null, email: cust.email };
  }

  // Faithful port of addressSummaryPage buildAttributionData (1996-2154). Reads the same
  // page globals (sessionCore / getMappedCartEdd / getLocationData / ensureUserIp / analytics),
  // sessionStorage (itemInSessionStorage, allFeesData), localStorage, and the shell customer.
  // IP is attribution-only and comes from an external service (api.ipify.org). Return the
  // cached value instantly if present, otherwise race the lookup against a short timeout so
  // a slow/blocked request can never hold up order placement (falls back to null).
  function userIpFast() {
    if (window._userIp) return Promise.resolve(window._userIp);
    if (!window.ensureUserIp) return Promise.resolve(null);
    return Promise.race([
      Promise.resolve(window.ensureUserIp()).catch(function () { return null; }),
      new Promise(function (resolve) { setTimeout(function () { resolve(window._userIp || null); }, 800); })
    ]);
  }

  function buildAttributionData(amountNum, paymentMethod, cart) {
    var cust = shellCustomer();
    var context = (window.sessionCore && window.sessionCore.getMergedContext) ? window.sessionCore.getMergedContext() : {};
    var parsedItems = [];
    try { parsedItems = JSON.parse(sessionStorage.getItem('itemInSessionStorage')) || []; } catch (e) {}
    var eddArray = window.getMappedCartEdd ? window.getMappedCartEdd() : [];
    var eddMap = Array.isArray(eddArray)
      ? eddArray.reduce(function (m, i) { if (i) m[i.sku_id] = i; return m; }, {})
      : {};
    var discAmt = localStorage.getItem('discountCode') ? (parseInt(localStorage.getItem('disc_price'), 10) || 0) : 0;
    var cartItems = inStock(cart.items);
    var totalAmt = sumLinePaise(cartItems) / 100.0;
    var allFeesData = null;
    try { allFeesData = JSON.parse(sessionStorage.getItem('allFeesData')); } catch (e) {}
    var totalFees = allFeesData
      ? Object.keys(allFeesData).reduce(function (s, k) { var it = allFeesData[k]; return s + (typeof it.fee === 'number' ? it.fee : 0); }, 0)
      : 0;
    var totalMrp = 0;
    cartItems.forEach(function (it) { totalMrp += (it.original_price || it.price) * it.quantity; });

    var updatedProducts = parsedItems.map(function (item) {
      // Prefer the per-item edd_response snapshot (written by checkoutStorageItems); fall back to
      // cartEddInfo only when present (the /cart page). Sticky checkout runs where cartEddInfo is absent.
      var eddResponse = (item && item.edd_response) || null;
      var skuIdToMatch = item && (item.item_sku || item.sku_id);
      if (!eddResponse && eddMap[skuIdToMatch]) { var rest = Object.assign({}, eddMap[skuIdToMatch]); delete rest.sku_id; eddResponse = rest; }
      var price = parseInt((item && item.price) || 0, 10);
      var disc = parseInt((item && item.discount) || 0, 10);
      var mrp = parseInt((item && (item.mrp || item.price)) || 0, 10);
      return {
        brand: (item && item.item_brand) || null,
        category: {
          l0: (item && item.item_category) || null, l1: (item && item.item_category2) || null,
          l2: (item && item.item_category3) || null, l3: (item && item.item_category4) || null,
          l4: (item && item.item_category5) || null
        },
        coupon_name: (item && item.coupon) || null,
        edd: (item && (item.eddText || item.edd)) || (eddResponse && eddResponse.delivery_timeslot_label) || null,
        edd_response: eddResponse || window.emptyEddResponse(),
        position: (item && item.index) || null,
        product_coupon_discount: (item && item.discount) ? parseInt(item.discount, 10) : null,
        product_id: (item && item.item_id) ? item.item_id.toString() : null,
        product_name: (item && item.item_name) || null,
        product_url: (item && item.url) || null,
        product_value_before_cart_discount: (price + disc) || null,
        product_value_mrp: mrp || null,
        product_value_post_cart_discount: (price - disc) || null,
        quantity: (item && item.quantity) ? parseInt(item.quantity, 10) : null,
        sku_id: (item && item.item_sku) || null,
        sku_mrp: mrp || null,
        sku_selling_price: (price + disc) || null,
        variant_id: (item && (item.item_variant || item.variant_id || item.item_id)) ? (item.item_id ? item.item_id.toString() : null) : null,
        variant_name: (item && item.item_variant) || null,
        variant_url: (item && item.url) || null
      };
    });

    var userLocationData = window.getLocationData ? window.getLocationData() : null;
    var defaultAttribution = {
      criteo_gum_id: null, fbclid: null, fbc: null, fbp: null, gbraid: null, gclid: null, wbraid: null,
      utm_ad: null, utm_adgroup: null, utm_adgroup_name: null, utm_adid: null, utm_adset: null, utm_adsetid: null,
      utm_campaign: null, utm_campaign_name: null, utm_content: null, utm_creative: null, utm_device: null,
      utm_geo: null, utm_landing: null, utm_matchtype: null, utm_medium: null, utm_network: null, utm_placement: null,
      utm_source: null, utm_targetid: null, utm_term: null, referrer_url: null,
      af_adid: null, af_adset: null, af_adsetid: null, af_campaign: null, af_campaign_name: null,
      af_primary_adid: null, af_primary_adset: null, af_primary_adsetid: null, af_primary_campaign: null,
      af_primary_campaign_name: null, af_primary_siteid: null, af_primary_source: null, af_siteid: null,
      af_source: null, ga_pseudo_id: null
    };
    var finalAttribution = Object.assign({}, defaultAttribution, (context.attribution || {}));
    var ord = (function () { try { return JSON.parse(sessionStorage.getItem('order_shipping_address')); } catch (e) { return null; } })();
    var guest = (function () { try { return JSON.parse(sessionStorage.getItem('guestAddress')); } catch (e) { return null; } })();

    return userIpFast().then(function (ip) {
      var segmentContext = {
        page: { path: location.pathname, referrer: document.referrer || null, search: location.search, title: document.title, url: location.href },
        userAgent: navigator.userAgent,
        locale: navigator.language || null,
        library: { name: 'analytics.js', version: (window.analytics && window.analytics.VERSION) || null },
        campaign: (context.attribution && context.attribution.utm_source) ? {
          source: context.attribution.utm_source, medium: context.attribution.utm_medium,
          name: context.attribution.utm_campaign, content: context.attribution.utm_content, term: context.attribution.utm_term
        } : {},
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        ip: ip || null,
        protocols: { sourceId: (window.analytics && window.analytics.settings && window.analytics.settings.writeKey) || null },
        device: (window.sessionCore && window.sessionCore.getDeviceInfo) ? window.sessionCore.getDeviceInfo() : null,
        traits: (window.sessionCore && window.sessionCore.getUserTraits) ? window.sessionCore.getUserTraits() : null
      };
      var data = {
        attribution: finalAttribution,
        cart_id: localStorage.getItem('cartId') || null,
        cart_key: localStorage.getItem('cartKey') || null,
        payment_id: '',
        payment_method: paymentMethod || sessionStorage.getItem('paymentMethod') || null,
        products: updatedProducts,
        order_coupon_used: localStorage.getItem('discountCode') || null,
        order_quantity: parsedItems.reduce(function (acc, item) { return acc + (parseInt(item.quantity, 10) || 1); }, 0),
        order_sku_unique_count: parsedItems.length,
        order_value: {
          card_discount: 0, cart_discount: discAmt, fees: totalFees, mrp: (totalMrp / 100) || 0,
          paid_by_customer: amountNum, pdp_discount: 0, post_cart_discount: totalAmt - discAmt,
          total_discount: discAmt, without_cart_discount: totalAmt
        },
        email: cust.email || (ord && ord.email) || (guest && guest.email) || null,
        phone: cust.phone || (ord && ord.phone) || (guest && guest.phone) || null,
        platform: 'web',
        session_id: context.session_id || null,
        session_index: context.session_index || null,
        user_id: cust.id ? String(cust.id) : null,
        anonymousId: (typeof rudderanalytics !== 'undefined' && rudderanalytics.getAnonymousId) ? rudderanalytics.getAnonymousId() : null,
        context: segmentContext
      };
      if (userLocationData) data.user_location = userLocationData;
      return data;
    });
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // The controller broadcasts the fresh cart on EVERY sticky-cart mutation (broadcastCartChange
  // → cart:updated). Cache it so the read-only Review render doesn't re-fetch /cart.js — that
  // round-trip is what made entering Review feel slow even when pricing was already settled.
  var cartCache = null;
  document.addEventListener('cart:updated', function (e) {
    cartCache = (e && e.detail && e.detail.cart) ? e.detail.cart : null;
  });

  // Total item count the drawer is currently displaying (server-rendered, authoritative).
  function drawerItemCount() {
    var r = document.querySelector('[data-st-content-root]');
    if (!r) return null;
    var v = parseInt(r.getAttribute('data-item-count'), 10);
    return isNaN(v) ? null : v;
  }

  // Fast cart for display-only paths (renderReview): use the in-memory cart kept fresh by
  // cart:updated, falling back to window.shopifyCart. BUT only trust it when its item_count
  // matches what the drawer is showing — window.shopifyCart can be empty/stale on a PDP, which
  // would render "0 Item" on Review. On any mismatch (or no cache) fetch the real /cart.js.
  // Order-critical paths (renderPayment / placeOrder) keep getCart()'s always-fresh fetch.
  function getCartFast() {
    var cached = (cartCache && Array.isArray(cartCache.items)) ? cartCache
      : (window.shopifyCart && Array.isArray(window.shopifyCart.items)) ? window.shopifyCart
      : null;
    var want = drawerItemCount();
    if (cached && want != null && cached.item_count === want) {
      cartCache = cached;
      return Promise.resolve(cached);
    }
    return getCart().then(function (c) { cartCache = c; return c; });
  }

  // Warm the cart cache in the BACKGROUND while the user is on the cart step, so entering Review
  // is instant — no /cart.js round-trip (and no review-panel spinner) on the proceed click.
  // No-op when the cache already matches the drawer; a single fetch otherwise. Pure cache fill —
  // it never re-renders, so it can't cause the "updating multiple times" churn.
  function prewarmCart() {
    var want = drawerItemCount();
    if (cartCache && Array.isArray(cartCache.items) && want != null && cartCache.item_count === want) return;
    if (window.shopifyCart && Array.isArray(window.shopifyCart.items) && want != null && window.shopifyCart.item_count === want) {
      cartCache = window.shopifyCart;
      return;
    }
    getCart().then(function (c) { cartCache = c; }).catch(function () {});
  }

  // In-stock cart lines only — OOS items are excluded from every price/payment payload.
  function inStock(items) {
    if (window.StickyCart && typeof window.StickyCart.filterInStock === 'function') {
      return window.StickyCart.filterInStock(items);
    }
    return items || [];
  }
  function sumQty(items) {
    return (items || []).reduce(function (s, it) { return s + (it.quantity || 0); }, 0);
  }
  function sumLinePaise(items) {
    return (items || []).reduce(function (s, it) {
      return s + (it.final_line_price != null ? it.final_line_price : (it.line_price || 0));
    }, 0);
  }

  function getLocation() {
    try { return JSON.parse(localStorage.getItem('userLocation')) || {}; } catch (e) { return {}; }
  }

  function getAddress() {
    var raw = sessionStorage.getItem('order_shipping_address') || sessionStorage.getItem('guestAddress');
    if (raw) { try { return JSON.parse(raw); } catch (e) {} }
    return null;
  }

  function formatINR(paise) {
    var r = Math.round((paise || 0) / 100);
    try { return '₹' + r.toLocaleString('en-IN'); } catch (e) { return '₹' + r; }
  }

  // Payable total in rupees — the fees module writes finalTotalAmount (subtotal +
  // fees − coupon). Fall back to the displayed footer price, then the subtotal.
  function payableRupees(scope) {
    var stored = sessionStorage.getItem('finalTotalAmount');
    if (stored != null && stored !== '') {
      var v = parseFloat(stored);
      if (!isNaN(v)) return Math.round(v);
    }
    var fp = scope.querySelector('[data-st-footer-price]');
    if (fp) {
      var n = parseInt((fp.textContent || '').replace(/[^\d]/g, ''), 10);
      if (!isNaN(n)) return n;
    }
    var root = scope.querySelector('[data-st-content-root]');
    return root ? Math.round((parseInt(root.getAttribute('data-subtotal'), 10) || 0) / 100) : 0;
  }

  function grandTotalPaise(scope) {
    return payableRupees(scope) * 100;
  }

  /* ---- Review (Figma 109-12967 / 111-13333) --------------------------- */
  // Orange ETA clock + blue vet-callout icons for the review groups.
  var REVIEW_CLOCK_SVG =
    '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M10.8379 3C6.43789 3 2.83789 6.6 2.83789 11C2.83789 15.4 6.43789 19 10.8379 19C15.2379 19 18.8379 15.4 18.8379 11C18.8379 6.6 15.2379 3 10.8379 3ZM12.4379 13.96C12.0379 14.2 11.5579 14.04 11.3179 13.64L10.1179 11.4C10.0379 11.24 10.0379 11.16 10.0379 11V7C10.0379 6.52 10.3579 6.2 10.8379 6.2C11.3₁79 6.2 ₁₁.6379 6.52 ₁₁.6379 7V₁₀.76L₁₂.7579 ₁₂.84C₁₂.9₁79 ₁₃.24 ₁₂.8₃79 ₁₃.72 ₁₂.4₃79 ₁₃.96Z" fill="url(#paint0_linear_₁₁₁_₁₃₃₄₀)" />' +
      '<defs>' +
        '<linearGradient id="paint0_linear_₁₁₁_₁₃₃₄₀" x１="₁₀.８₃７₉" y１="₃" x２="₁₀.８₃７₉" y２="₁₉" gradientUnits="userSpaceOnUse">' +
          '<stop stop-color="#FFBF8B" />' +
          '<stop offset="１" stop-color="#FF7A0C" />' +
        '</linearGradient>' +
      '</defs>' +
    '</svg>';

  var REVIEW_VET_SVG =
    '<svg class="st-cart__review-vet-ic" width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<g clip-path="url(#clip0_111_16437)">' +
        '<path d="M12.75 3.54167L9.20833 0H1.41667C1.04094 0 0.680609 0.149256 0.414932 0.414932C0.149255 0.680609 0 1.04094 0 1.41667V15.5833C0 15.9591 0.149255 16.3194 0.414932 16.5851C0.680609 16.8507 1.04094 17 1.41667 17H11.3333C11.7091 17 12.0694 16.8507 12.3351 16.5851C12.6007 16.3194 12.75 15.9591 12.75 15.5833V3.54167Z" fill="url(#paint0_linear_111_16437)" />' +
        '<path opacity="0.1" d="M11.3333 16.8229H1.41667C1.04094 16.8229 0.680609 16.6737 0.414932 16.408C0.149255 16.1423 0 15.782 0 15.4062V15.5833C0 15.9591 0.149255 16.3194 0.414932 16.5851C0.680609 16.8507 1.04094 17 1.41667 17H11.3333C11.7091 17 12.0694 16.8507 12.3351 16.5851C12.6007 16.3194 12.75 15.9591 12.75 15.5833V15.4062C12.75 15.782 12.6007 16.1423 12.3351 16.408C12.0694 16.6737 11.7091 16.8229 11.3333 16.8229Z" fill="#010101" />' +
        '<path d="M9.20898 0V2.125C9.20898 2.50072 9.35824 2.86106 9.62392 3.12673C9.88959 3.39241 10.2499 3.54167 10.6257 3.54167H12.7507L9.20898 0Z" fill="#5D86C9" />' +
        '<path opacity="0.2" d="M9.20833 0H1.41667C1.04094 0 0.680609 0.149256 0.414932 0.414932C0.149255 0.680609 0 1.04094 0 1.41667V1.59375C0 1.21803 0.149255 0.857692 0.414932 0.592015C0.680609 0.326339 1.04094 0.177083 1.41667 0.177083H9.20833V0Z" fill="white" />' +
        '<path opacity="0.05" d="M10.6257 3.36442C10.2499 3.36442 9.88959 3.21517 9.62392 2.94949C9.35824 2.68381 9.20898 2.32348 9.20898 1.94775V2.12484C9.20898 2.50056 9.35824 2.8609 9.62392 3.12657C9.88959 3.39225 10.2499 3.5415 10.6257 3.5415H12.7507L12.5736 3.36442H10.6257Z" fill="#010101" />' +
        '<path d="M10.625 3.54142C10.2354 3.54142 9.88337 3.38417 9.62695 3.12988L12.75 6.25222V3.54142H10.625Z" fill="url(#paint1_linear_111_16437)" />' +
        '<path d="M9.56185 9.2085H8.62968L7.52539 10.6223L5.9196 8.38612C6.25971 8.26217 6.55361 8.03685 6.7616 7.74057C6.96959 7.44429 7.08166 7.09132 7.08268 6.72933C7.08268 5.75325 6.28793 4.9585 5.31185 4.9585H3.54102V10.271H4.24935V8.50016H5.12981L7.06781 11.2088L5.31185 13.4585H6.24402L7.51335 11.8322L8.67643 13.4585H9.56185L7.97235 11.245L9.56185 9.2085ZM4.24935 5.66683H5.31185C5.89764 5.66683 6.37435 6.14354 6.37435 6.72933C6.37435 7.31512 5.89764 7.79183 5.31185 7.79183H4.24935V5.66683Z" fill="white" />' +
        '<path d="M9.20833 0H1.41667C1.04094 0 0.680609 0.149256 0.414932 0.414932C0.149255 0.680609 0 1.04094 0 1.41667V15.5833C0 15.9591 0.149255 16.3194 0.414932 16.5851C0.680609 16.8507 1.04094 17 1.41667 17H11.3333C11.7091 17 12.0694 16.8507 12.3351 16.5851C12.6007 16.3194 12.75 15.9591 12.75 15.5833V3.54167L9.20833 0Z" fill="url(#paint2_linear_111_16437)" />' +
      '</g>' +
      '<defs>' +
        '<linearGradient id="paint0_linear_111_16437" x1="6.375" y1="0" x2="6.375" y2="17" gradientUnits="userSpaceOnUse">' +
          '<stop stop-color="#93B7F1" />' +
          '<stop offset="1" stop-color="#5D86C7" />' +
        '</linearGradient>' +
        '<linearGradient id="paint1_linear_111_16437" x1="10.3048" y1="2.45201" x2="13.4279" y2="5.57505" gradientUnits="userSpaceOnUse">' +
          '<stop stop-color="#010101" stop-opacity="0.1" />' +
          '<stop offset="1" stop-color="#010101" stop-opacity=x"0" />' +
        '</linearGradient>' +
        '<linearGradient id="paint2_linear_111_16437" x1="-1.91321" y1="5.20412" x2="14.2269" y2="12.7302" gradientUnits="userSpaceOnUse">' +
          '<stop stop-color="white" stop-opacity="0.2" />' +
          '<stop offset="1" stop-color="white" stop-opacity="0" />' +
        '</linearGradient>' +
        '<clipPath id="clip0_111_16437">' +
          '<rect width="12.75" height="17" fill="white" />' +
        '</clipPath>' +
      '</defs>' +
    '</svg>'

  // Review mirrors the cart's delivery grouping but with a stacked header
  // (orange clock + "Arriving …" + "Delivery N of M") and read-only item cards.
  // The Review bill is a CLONE of the live cart bill, taken when entering Review. On a post-login
  // jump straight to Review the clone can be made BEFORE the fees module finalizes the total, so
  // it shows a stale (pre-coupon / pre-fee) total while the footer is already correct. The fees /
  // coupon modules dispatch 'stcart:pricing-updated' when they settle — copy the freshly-computed
  // values from the live cart bill into the review clone so the two never disagree.
  function refreshReviewBill() {
    var live = document.querySelector('[data-st-bill]');
    var clone = document.querySelector('.st-cart__review-bill-card');
    if (!live || !clone) return;
    ['[data-st-bill-total-value]', '[data-st-bill-coupon]'].forEach(function (sel) {
      var s = live.querySelector(sel), d = clone.querySelector(sel);
      if (s && d) d.innerHTML = s.innerHTML;
    });
    var sf = live.querySelector('[data-st-fees]'), df = clone.querySelector('[data-st-fees]');
    if (sf && df) df.innerHTML = sf.innerHTML;
    var ss = live.querySelector('[data-st-bill-savings]'), ds = clone.querySelector('[data-st-bill-savings]');
    if (ss && ds) { ds.innerHTML = ss.innerHTML; ds.hidden = ss.hidden; }
  }
  document.addEventListener('stcart:pricing-updated', refreshReviewBill);

  // The item cards are CLONED from the cart step (so prices / struck MRP / tags
  // are exactly the server-rendered values) with the qty stepper removed.
  function renderReview(scope) {
    var panel = scope.querySelector('[data-st-review-panel]');
    if (!panel) return;
    panel.innerHTML = '<div class="st-cart__review-loading"><span class="st-cart__spinner" aria-hidden="true"></span></div>';

    // Pre-warm the external IP lookup (attribution only) so it's cached well before the
    // user places the order — keeps api.ipify.org off the order-placement critical path.
    if (window.ensureUserIp) { try { window.ensureUserIp(); } catch (e) {} }

    var root = scope.querySelector('[data-st-content-root]');
    var rxLines = (root ? root.getAttribute('data-rx-lines') || '' : '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var pharmaMsg = {};
    try { pharmaMsg = JSON.parse(sessionStorage.getItem('stPharmaMsg')) || {}; } catch (e) {}

    // Templates for the review cards: one per SKU (skip cart-screen split clones).
    var nodesBySku = {};
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-st-items] .st-cart__item[data-skuid]'),
      function (n) { var sku = n.getAttribute('data-skuid'); if (sku && !nodesBySku[sku]) nodesBySku[sku] = n; }
    );
    // Fallback map: cart line index → node (used when there's no shipmentInfo).
    var nodesByLine = {};
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-st-items] .st-cart__item[data-line]'),
      function (n) { nodesByLine[n.getAttribute('data-line')] = n; }
    );

    getCartFast().then(function (cart) {
      var loc = getLocation();

      (cart.items || []).forEach(function (it, idx) { it.__line = idx + 1; });
      var reviewItems = inStock(cart.items);

      panel.innerHTML = '';
      panel.appendChild(reviewTitle('Delivery estimates'));

      var shipments = reviewShipments(loc, reviewItems);
      if (shipments && shipments.length) {
        var itemBySku = {};
        reviewItems.forEach(function (it) { if (!itemBySku[it.sku]) itemBySku[it.sku] = it; });
        renderReviewShipments(panel, shipments, itemBySku, nodesBySku, rxLines, pharmaMsg);
      } else {
        renderReviewByTime(panel, reviewItems, nodesByLine, rxLines, pharmaMsg, loc);
      }

      // Bill card — clone (computed fees/total/savings) + retitle per Figma.
      var bill = scope.querySelector('[data-st-bill]');
      if (bill) {
        var billClone = bill.cloneNode(true);
        billClone.removeAttribute('data-st-bill');
        billClone.classList.add('st-cart__review-bill-card');
        var t = billClone.querySelector('.st-cart__bill-title');
        if (t) {
          var n = sumQty(reviewItems);
          t.textContent = 'Bill Price Details (' + n + ' Item' + (n > 1 ? 's' : '') + ')';
        }
        panel.appendChild(billClone);
      }

      // Analytics: Review Step Viewed + per-shipment Delivery Estimate Viewed.
      var A = window.StickyCartAnalytics;
      if (A) {
        var addr; try { addr = JSON.parse(sessionStorage.getItem('order_shipping_address')); } catch (e) {}
        var pin = addr ? (addr.zip || addr.pincode || null) : (loc && loc.pincode) || null;
        var groups = panel.querySelectorAll('.st-cart__review-group');
        if (typeof A.reviewStepViewed === 'function') {
          A.reviewStepViewed(shipments && shipments.length ? shipments.length : (groups.length || 1), pin);
        }
        if (typeof A.deliveryEstimateViewed === 'function' && typeof A.observeImpression === 'function') {
          Array.prototype.forEach.call(groups, function (g, i) {
            var eta = (g.querySelector('.st-cart__review-eta') || {}).textContent || '';
            var hasRx = !!g.querySelector('.st-cart__review-vet');
            A.observeImpression(g, function () { A.deliveryEstimateViewed(i + 1, groups.length, eta, false, hasRx, hasRx); });
          });
        }
      }
    });
  }

  function reviewTitle(text) {
    var h = document.createElement('h3');
    h.className = 'st-cart__review-title';
    h.textContent = text;
    return h;
  }

  function stMoney(paise) {
    var r = Math.round((paise || 0) / 100);
    try { return '₹' + r.toLocaleString('en-IN'); } catch (e) { return '₹' + r; }
  }

  // Read the shipmentInfo cached by the cart-screen EDD module for this exact cart.
  function reviewShipments(loc, reviewItems) {
    if (!loc || !loc.pincode) return null;
    var key = loc.pincode + '|' + reviewItems.map(function (it) { return it.sku + ':' + it.quantity; }).sort().join(',');
    var cache;
    try { cache = JSON.parse(sessionStorage.getItem('stCartShipments')) || {}; } catch (e) { cache = {}; }
    var resp = cache[key];
    return (resp && Array.isArray(resp.shipmentInfo)) ? resp.shipmentInfo : null;
  }

  // Clone a cart node into a read-only review card. When qty is provided (split line),
  // show the partial qty + partial price from the per-unit data attrs.
  function reviewCloneRow(template, qty) {
    var clone = template.cloneNode(true);
    Array.prototype.forEach.call(
      clone.querySelectorAll('[data-st-qty], .st-cart__item-special-tag, .st-cart__item-oos-tag, .st-cart__item-rx-tag'),
      function (el) { el.parentNode && el.parentNode.removeChild(el); }
    );
    if (qty != null) {
      var unit = parseInt(clone.getAttribute('data-unit-price'), 10);
      if (!isNaN(unit)) { var sell = clone.querySelector('.st-cart__item-selling'); if (sell) sell.textContent = stMoney(unit * qty); }
      var mrp = parseInt(clone.getAttribute('data-unit-mrp'), 10);
      var mrpEl = clone.querySelector('.st-cart__item-mrp');
      if (mrpEl && !isNaN(mrp) && mrp > 0) mrpEl.textContent = 'MRP ' + stMoney(mrp * qty);
    }
    return clone;
  }

  // Nest the vet note INSIDE the pharmacy review row, below the product. The item's
  // existing children (media + info) are moved into a row wrapper so the li can become a
  // clean column (wrapper on top, vet below) — avoids flex-wrap layout glitches.
  function appendVet(row, vet) {
    if (!row || !vet) return;
    var inner = document.createElement('div');
    inner.className = 'st-cart__review-row';
    while (row.firstChild) inner.appendChild(row.firstChild);
    row.appendChild(inner);
    var v = document.createElement('div');
    v.className = 'st-cart__review-vet';
    v.innerHTML = vet;
    row.appendChild(v);
  }

  function reviewGroupCard(time, idx, total) {
    var card = document.createElement('div');
    card.className = 'st-cart__review-group';
    var head = document.createElement('div');
    head.className = 'st-cart__review-grouphead';
    head.innerHTML = REVIEW_CLOCK_SVG +
      '<div class="st-cart__review-grouphead-text">' +
        '<span class="st-cart__review-eta">' + escapeHtml(time || 'Delivery') + '</span>' +
        '<span class="st-cart__review-deliv">Delivery ' + (idx + 1) + ' of ' + total + '</span>' +
      '</div>';
    card.appendChild(head);
    var ul = document.createElement('ul');
    ul.className = 'st-cart__review-items';
    card.appendChild(ul);
    card._ul = ul;
    return card;
  }

  // Group review by shipmentInfo (warehouse + EDD); a split SKU appears once per shipment
  // with its partial qty + partial price.
  function renderReviewShipments(panel, shipments, itemBySku, nodesBySku, rxLines, pharmaMsg) {
    var sorted = shipments.slice().sort(function (a, b) {
      return new Date(a.deliveryDateTime || 0) - new Date(b.deliveryDateTime || 0);
    });
    var shipCount = {};
    sorted.forEach(function (sh) { (sh.items || []).forEach(function (it) { shipCount[it.skuId] = (shipCount[it.skuId] || 0) + 1; }); });
    var total = sorted.length;

    sorted.forEach(function (sh, idx) {
      // Show the full "Arriving by …" copy when present; fall back to the bare
      // delivery time, then a generic label.
      var card = reviewGroupCard(sh.webCheckoutMessage || sh.deliveryTime, idx, total);
      var any = false;
      (sh.items || []).forEach(function (it) {
        var cartItem = itemBySku[it.skuId];
        var template = nodesBySku[it.skuId];
        if (!cartItem || !template) return;
        any = true;
        var split = shipCount[it.skuId] > 1;
        var row = reviewCloneRow(template, split ? it.qty : null);
        appendVet(row, vetHtml(cartItem, rxLines, pharmaMsg)); // vet note nested inside the pharmacy row
        card._ul.appendChild(row);
      });
      if (any) panel.appendChild(card);
    });
  }

  // Fallback: per-SKU grouping by delivery time (when shipmentInfo is unavailable).
  function renderReviewByTime(panel, reviewItems, nodesByLine, rxLines, pharmaMsg, loc) {
    var cache = {};
    try { cache = JSON.parse(sessionStorage.getItem('stCartEddCache')) || {}; } catch (e) {}
    var groups = {};
    var order = [];
    reviewItems.forEach(function (it) {
      var rec = cache[(loc.pincode || '') + '|' + it.sku];
      var key = rec && rec.time ? rec.time : '__none__';
      if (!groups[key]) { groups[key] = { rec: rec, items: [] }; order.push(key); }
      groups[key].items.push(it);
    });
    var total = order.length;
    order.forEach(function (key, idx) {
      var g = groups[key];
      var card = reviewGroupCard(g.rec && (g.rec.msg || g.rec.time), idx, total);
      g.items.forEach(function (it) {
        var node = nodesByLine[String(it.__line)];
        if (!node) return;
        var row = reviewCloneRow(node, null);
        appendVet(row, vetHtml(it, rxLines, pharmaMsg)); // vet note nested inside the pharmacy row
        card._ul.appendChild(row);
      });
      panel.appendChild(card);
    });
  }

  function vetHtml(it, rxLines, pharmaMsg) {
    if (rxLines.indexOf(String(it.__line)) < 0) return '';
    var uploaded = it.properties && it.properties.prescription_file;
    var msg = uploaded
      ? (pharmaMsg.isAdded || 'Your prescription will be verified after the order is placed.')
      : (pharmaMsg.isNotAdded || 'Vet will call <b>within 10 minutes</b> of order confirmation.');
    // msg is backend copy that may contain markup (e.g. "<b>within 10 minutes</b>"); the
    // fallback bolds "within 10 minutes" to match the design (Figma).
    return REVIEW_VET_SVG + '<span>' + msg + '</span>';
  }

  /* ---- Payment methods -------------------------------------------------- */
  function renderPayment(scope) {
    var panel = scope.querySelector('[data-st-payment-panel]');
    if (!panel) return;
    panel.innerHTML =
      '<h3 class="st-cart__pay-title">Select a payment method</h3>' +
      '<p class="st-cart__pay-sub">Help us ensure a smooth delivery</p>' +
      '<div class="st-cart__pay-list" data-st-pay-list>' +
        '<div class="st-cart__review-loading"><span class="st-cart__spinner" aria-hidden="true"></span></div>' +
      '</div>';

    getCart().then(function (cart) {
      var body = {
        items: inStock(cart.items),
        AOV: payableRupees(scope),
        couponTitle: localStorage.getItem('discountCode')
      };
      return fetch(PAYMENT_METHODS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) { return r.json(); });
    }).then(function (data) {
      var methods = (data && data.paymentMethods && data.paymentMethods.methods) || [];
      methods.sort(function (a, b) { return (a.priority || 0) - (b.priority || 0); });
      var list = panel.querySelector('[data-st-pay-list]');
      if (!list) return;
      if (!methods.length) {
        list.innerHTML = '<p class="st-cart__coupon-empty">No payment methods available right now.</p>';
        return;
      }
      list.innerHTML = methods.map(payCardHtml).join('');
      wirePay(scope, list, methods);
      // Analytics: Payment Step Viewed (available methods + bank-offer flag).
      var A = window.StickyCartAnalytics;
      if (A && typeof A.paymentStepViewed === 'function') {
        var pm = data && data.paymentMethods;
        var hasBank = !!(pm && (pm.has_bank_offer || pm.hasBankOffer)) ||
          methods.some(function (m) { return /bank|cashback|offer/i.test(m.helperText || ''); });
        var addr; try { addr = JSON.parse(sessionStorage.getItem('order_shipping_address')); } catch (e) {}
        A.paymentStepViewed(
          methods.map(function (m) { return m.methodName; }),
          hasBank,
          addr ? (addr.zip || addr.pincode || null) : null
        );
      }
    }).catch(function (err) {
      if (window.console) console.error('[StickyCart] payment-methods', err);
    });
  }

  // Is this a Cash-on-Delivery method, and what fee (₹) does its helper text advertise?
  function isCod(name) { return /pay on delivery|cash on delivery|\bcod\b/i.test(name || ''); }
  function feeFromText(text) {
    var m = /₹\s*(\d+)/.exec(text || '');
    return m ? parseInt(m[1], 10) : null;
  }

  function payCardHtml(m) {
    var disabled = !!m.disabled;
    var left = m.leftIcon ? '<img class="st-cart__pay-licon" src="' + escapeHtml(m.leftIcon) + '" alt="" loading="lazy">' : '';
    var right = (m.rightIcon && m.rightIcon !== '') ? '<img class="st-cart__pay-ricon" src="' + escapeHtml(m.rightIcon) + '" alt="" loading="lazy">' : '';
    var sub = m.helperText ? '<span class="st-cart__pay-sub2">' + escapeHtml(m.helperText) + '</span>' : '';
    return (
      '<button type="button" class="st-cart__pay-card' + (disabled ? ' is-disabled' : '') + '"' +
      ' data-code="' + escapeHtml(m.methodName) + '"' + (disabled ? ' disabled' : '') + '>' +
        left +
        '<span class="st-cart__pay-text"><span class="st-cart__pay-name">' + escapeHtml(m.methodName) + '</span>' + sub + '</span>' +
        right +
        '<svg class="st-cart__pay-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>' +
      '</button>'
    );
  }

  function wirePay(scope, list, methods) {
    var byName = {};
    (methods || []).forEach(function (m) { byName[m.methodName] = m; });
    var cards = list.querySelectorAll('[data-code]');
    Array.prototype.forEach.call(cards, function (card) {
      card.addEventListener('click', function () {
        var code = card.getAttribute('data-code');
        sessionStorage.setItem('paymentMethod', code);
        var A = window.StickyCartAnalytics;
        if (A && typeof A.addPaymentInfo === 'function') A.addPaymentInfo(scope, code);
        // Spec: Payment Method Selected (+ COD fee details) and COD Fee Warning.
        var m = byName[code] || {};
        var codFee = isCod(code) ? feeFromText(m.helperText) : null;
        var hasBank = /bank|cashback|offer/i.test(m.helperText || '');
        if (A && typeof A.paymentMethodSelected === 'function') {
          A.paymentMethodSelected(code, codFee != null, codFee, hasBank);
        }
        if (codFee != null && A && typeof A.codFeeWarning === 'function') A.codFeeWarning(codFee);
        placeOrder(scope, code, card);
      });
    });
  }

  /* ---- Order placement (faithful replica of addressSummaryPage.liquid) --- */
  // Soft kill-switch only — set window.STICKY_CART_ALLOW_ORDER = false to disable.
  function placeOrder(scope, code, card) {
    if (window.STICKY_CART_ALLOW_ORDER === false) {
      alert('Checkout is temporarily unavailable. Please try again shortly.');
      return;
    }
    card.classList.add('is-busy');
    getCart().then(function (cart) {
      var amountNum = payableRupees(scope); // RUPEES (final payable), like the existing `.final_price`
      // Populate itemInSessionStorage BEFORE building attribution / redirecting, so both the
      // attribution payload and the off-site page.checking-payment.liquid (GA `purchase` + CleverTap
      // `Charged`) have the ordered items. Owned by the analytics module (single [data-st-analytics]
      // parser + edd/coupon lookups). Without this, prepaid orders fire no `Charged`.
      var A = window.StickyCartAnalytics;
      if (A && typeof A.persistCheckoutItems === 'function') A.persistCheckoutItems();
      return buildAttributionData(amountNum, code, cart).then(function (attributionData) {
        var ship = buildShippingAddress();
        var handleCart = null;
        try { handleCart = JSON.parse(localStorage.getItem('handle-cart')); } catch (e) {}
        var cartObj = buildCartObject(cart, ship, amountNum, handleCart);
        if (code === 'Pay On Delivery') return placeCOD(cartObj, attributionData);
        return initiateJuspay(cartObj, code, amountNum, attributionData);
      });
    }).catch(function (err) {
      if (window.console) console.error('[StickyCart] placeOrder', err);
      card.classList.remove('is-busy');
    });
  }

  // The cart object sent to both /juspay/initiate and /create-cod-order (addressSummaryPage 2379-2401 / 2504-2526).
  function buildCartObject(cart, ship, amountNum, handleCart) {
    var disc = handleCart && handleCart.checkoutDiscountCodeApplyV2 && handleCart.checkoutDiscountCodeApplyV2.checkout;
    // Only in-stock lines are charged / shipped — OOS items never enter the order.
    // original_price carries the real MRP (from handle-cart) — the backend reads it for
    // the order's compare_at_price property. Copies, never the live cart objects; see
    // assets/cart-mrp.js.
    var items = inStock(cart.items);
    if (window.getCartItemsWithMrp) items = window.getCartItemsWithMrp(items, disc);
    return {
      id: handleCart ? handleCart.id : null,
      checkoutUrl: handleCart ? handleCart.checkoutUrl : null,
      note: null,
      zip: ship.addr ? ship.addr.zip : '',
      attributes: {},
      original_total_price: amountNum,
      total_price: amountNum,
      total_discount: cart.total_discount,
      total_weight: cart.total_weight,
      item_count: sumQty(items),
      items: items,
      discountApplications: disc && disc.discountApplications ? disc.discountApplications : null,
      lineItems: disc && disc.lineItems ? disc.lineItems : null,
      requires_shipping: true,
      currency: 'INR',
      items_subtotal_price: amountNum,
      cart_level_discount_applications: [],
      checkout_charge_amount: amountNum,
      shippingAddress: ship.addr,
      email: ship.email || ''
    };
  }

  function placeCOD(cartObj, attributionData) {
    return fetch(COD_ORDER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cart: cartObj, isWebOrder: true, attributionData: attributionData })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        var order = res && res.response && res.response.order;
        if (order && order.customer && order.customer.id) {
          // Spec: Order Placed (COD) — confirmed in-drawer, fired before the redirect.
          var A = window.StickyCartAnalytics;
          if (A && typeof A.orderPlaced === 'function') {
            A.orderPlaced({
              transaction_id: order.name || order.token || null,
              payment_method: 'Pay On Delivery',
              total_value: cartObj.total_price,
              total_discount: cartObj.total_discount,
              coupon_code: localStorage.getItem('discountCode') || null,
              coupon_discount: parseInt(localStorage.getItem('disc_price'), 10) || 0
            });
          }
          window.isPaymentRedirecting = true;
          sessionStorage.removeItem('guestAddress');
          sessionStorage.removeItem('guestCheckout');
          // Clear the cart without blocking the redirect: keepalive lets the request finish
          // after we navigate away, so the user reaches the thank-you page immediately.
          try { fetch('/cart/clear', { method: 'GET', keepalive: true }).catch(function () {}); } catch (e) {}
          window.location.href = THANKYOU_URL + '?token=' + encodeURIComponent(order.token) + '&client=web';
          return;
        }
        return res;
      });
  }

  function initiateJuspay(cartObj, code, amountNum, attributionData) {
    return fetch(JUSPAY_INITIATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cart: cartObj, paymentMethod: code, amount: String(amountNum), attributionData: attributionData })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        var link = res && res.response && res.response.payment_links && res.response.payment_links.web;
        if (link) {
          // Paid order: payment completes off-site, so page.checking-payment.liquid fires the
          // gated "Sticky Cart Order Placed" on poll-success. Flag it as a sticky-cart order
          // (+ stash coupon context the cart is about to be cleared of).
          try {
            sessionStorage.setItem('stOrderSource', 'sticky_cart_desktop');
            sessionStorage.setItem('stOrderCoupon', localStorage.getItem('discountCode') || '');
            sessionStorage.setItem('stOrderCouponDiscount', localStorage.getItem('disc_price') || '0');
            sessionStorage.setItem('stOrderPaymentMethod', code || '');
          } catch (e) {}
          window.isPaymentRedirecting = true;
          window.location.href = link; // → Juspay page → page.checking-payment.liquid → thank-you
        }
        return res;
      });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  window.StickyCartCheckout = { renderReview: renderReview, renderPayment: renderPayment, prewarmCart: prewarmCart };
})();
